import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Checkbox, Icon, Label, Menu, Popup, Table } from 'semantic-ui-react';
import { wrapConsumer } from '../FilteringContextProvider';
import { filterStateType } from '../../../types';
import SortOptions from './SortOption';
import {
  entityType as et,
  FILTER_EMPTY_PLACEHOLDER,
  FILTER_NOT_EMPTY_PLACEHOLDER,
  filterFieldSuffix
} from '../../../constants';

const OverviewTableHeader = ({
  filterState,
  handleFilterStateChanged,
  handleApplyFiltering,
  tableOptions,
  renderSorting,
  renderSelection,
  selectedRows,
  allRowsSelected,
  onSelectAllRows,
  entityType,
  isHasAnyActionRequiredRecord,
  t
}) => {
  const MAX_EXPANDED_COLS_NUMBER = 10;
  const [expandedColumn, setExpandedColumn] = React.useState([]);
  const [openContextMenu, setOpenContextMenu] = React.useState(false);
  const [contextMenuPosition, setContextMenuPosition] = React.useState(false);
  const [contextMenuColumn, setContextMenuColumn] = React.useState({});
  const visibleColumns = filterState.visibleColumns;
  let contextRef = useRef();
  const optionsWidth =
    tableOptions.length === 1 ? '35px' : tableOptions.length === 2 ? '66px' : '127px';
  const largeSizeColumns = [
    'GEN_TRADE_NAME',
    'GEN_CHEMICAL_NAME',
    'GEN_CONTACTS',
    'GEN_REMARKS',
    'GEN_SDS_FILES',
    'CHM_HAZARD_STATEMENT'
  ];

  useEffect(() => {
    const cells = document.querySelectorAll('.tableCell');
    cells.forEach(cell => {
      if (cell.offsetWidth < cell.scrollWidth) {
        cell.title = cell.textContent;
      }
    });
  });

  const getFilterValueColumn = col => {
    return filterState.filterValues ? filterState.filterValues[col] : null;
  };

  const isEmptyFilterValueColumn = col => {
    return getFilterValueColumn(col) === FILTER_EMPTY_PLACEHOLDER;
  };

  const isNotEmptyFilterValueColumn = col => {
    return getFilterValueColumn(col) === FILTER_NOT_EMPTY_PLACEHOLDER;
  };

  const handleContextMenu = (e, columnKey) => {
    e.preventDefault();
    setOpenContextMenu(!openContextMenu);
    setContextMenuColumn(columnKey);
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
  };
  const handlePopupClose = React.useCallback(() => setOpenContextMenu(false), []);

  const emptyNonEmptyFilterHandler = (placeholder, isClear) => {
    setOpenContextMenu(!openContextMenu);
    let values = filterState.filterValues ? filterState.filterValues : {};

    const type = filterState.visibleColumns.filter(col => col.key === contextMenuColumn).map(col => (col.filterType.type));
    if(type.includes("NUMERIC") || type.includes("DATE")){
      delete values[contextMenuColumn + filterFieldSuffix.more_than];
      delete values[contextMenuColumn + filterFieldSuffix.less_than];
      delete values[contextMenuColumn];
    } else {
      delete values[contextMenuColumn];
    }
    if (!isClear) values[contextMenuColumn] = placeholder;

    handleApplyFiltering(values);
  };

  const getColumnSizeClass = (columnName, expandedColumn) => {
    if (expandedColumn.includes(columnName)) {
      return largeSizeColumns.includes(columnName) ? ' expandedColumnLarge' : ' expandedColumn';
    } else {
      if (et.building === entityType) {
        if (columnName === 'GEN_TRADE_NAME') return ' columnLargeByDefault';
        if (
          columnName === 'GEN_LOCATION_NAME' ||
          columnName === 'GEN_QUANTITY' ||
          columnName === 'CHM_IS_AEROSOL'
        )
          return ' columnSmallByDefault';
      } else {
        return '';
      }
    }
  };

  return (
    <Table.Header>
      <Table.Row>
        {renderSelection && (
          <Table.HeaderCell key={'selection' + entityType} style={{ width: '35px' }}>
            <Checkbox
              id={'selectAll' + entityType}
              name={'selectAll' + entityType}
              onChange={() => {
                onSelectAllRows();
              }}
              indeterminate={Object.keys(selectedRows).length > 0 && !allRowsSelected}
              checked={allRowsSelected}
            />
          </Table.HeaderCell>
        )}

        {isHasAnyActionRequiredRecord && (<Table.HeaderCell id="warning-not-checked" style={{ width: '38px' }} />)}

        {tableOptions.length > 0 && (
          <Table.HeaderCell id="options" style={{ width: optionsWidth }} />
        )}
        {visibleColumns.map(entry => {
          let columnName = entry.key;
          let showOnlyEmpty = isEmptyFilterValueColumn(columnName);
          let showOnlyNonEmpty = isNotEmptyFilterValueColumn(columnName);
          return (
            <Table.HeaderCell
              draggable
              key={columnName}
              id={columnName}
              onContextMenu={e => handleContextMenu(e, columnName)}
              className={
                'tableCell draggableTHeader disableTouchZoom' +
                getColumnSizeClass(columnName, expandedColumn)
              }
              onDoubleClick={() => {
                let expandedColumnCopy = JSON.parse(JSON.stringify(expandedColumn));
                const indexOfColumn = expandedColumnCopy.lastIndexOf(columnName);
                if (indexOfColumn === -1) {
                  expandedColumnCopy.push(columnName);
                } else {
                  expandedColumnCopy.splice(indexOfColumn, 1);
                }
                if (expandedColumnCopy.length > MAX_EXPANDED_COLS_NUMBER) {
                  expandedColumnCopy.shift();
                }
                setExpandedColumn(expandedColumnCopy);
              }}
              onDragStart={e => {
                e.dataTransfer.setData('dragStart', e.target.id);
              }}
              onDragOver={e => {
                if (e.target.tagName === 'TH') {
                  document.getElementById(e.target.id).style.backgroundColor = '#e1e2e3';
                }
                e.preventDefault();
              }}
              onDragLeave={e => {
                document.getElementById(e.target.id).style.backgroundColor = '#f9fafb';
              }}
              onDrop={e => {
                let dragStart = e.dataTransfer.getData('dragStart');
                let dragEnd = e.target.id;
                let from = visibleColumns.map(col => col.value).lastIndexOf(dragStart);
                let to = visibleColumns.map(col => col.value).lastIndexOf(dragEnd);
                document.getElementById(e.target.id).style.backgroundColor = '#f9fafb';
                if (e.target.tagName === 'TH') {
                  let columns = visibleColumns;
                  columns.splice(to, 0, columns.splice(from, 1)[0]);
                  handleFilterStateChanged({ visibleColumns: columns });
                }
              }}
            >
              {renderSorting && (
                <SortOptions
                  sortField={columnName}
                  handleSortChange={handleFilterStateChanged}
                  sortDirection={filterState.sort.sortDirection}
                  activeSortField={filterState.sort.sortField}
                />
              )}

              {(showOnlyEmpty || showOnlyNonEmpty) && (
                <Label
                  circular
                  color={showOnlyEmpty ? 'orange' : 'olive'}
                  empty
                  key={columnName}
                  size={'mini'}
                  title={
                    t(entry.label) +
                    (showOnlyEmpty
                      ? t('table.header.tooltip.empty.values')
                      : t('table.header.tooltip.non.empty.values'))
                  }
                  style={{ marginRight: '3px', cursor: 'pointer' }}
                />
              )}
              {t(entry.label)}
            </Table.HeaderCell>
          );
        })}
      </Table.Row>
      {renderSelection && (
        <Popup
          ref={contextRef}
          onClose={handlePopupClose}
          open={openContextMenu}
          className="contextMenu"
          style={{
            left: contextMenuPosition.x,
            top: contextMenuPosition.y
          }}
        >
          <Menu secondary vertical className="contextSubMenu">
            <Menu.Item
              key="hideColumn"
              onClick={() => {
                setOpenContextMenu(!openContextMenu);
                let toHide = visibleColumns.map(col => col.value).lastIndexOf(contextMenuColumn);
                let columns = visibleColumns;
                columns.splice(toHide, 1);
                if (filterState.filterValues) delete filterState.filterValues[contextMenuColumn];
                handleFilterStateChanged({
                  visibleColumns: columns
                });
              }}
            >
              <Icon name="eye slash outline" />
              {t('table.header.option.hide.column')}
            </Menu.Item>

            <Menu.Item
              key="filterEmptyValuesColumn"
              onClick={() =>
                emptyNonEmptyFilterHandler(
                  FILTER_EMPTY_PLACEHOLDER,
                  isEmptyFilterValueColumn(contextMenuColumn)
                )
              }
            >
              <Icon name="filter" color="orange" />
              {!isEmptyFilterValueColumn(contextMenuColumn)
                ? t('table.header.option.filter.empty.values')
                : t('table.header.option.filter.empty.values.remove')}
            </Menu.Item>

            <Menu.Item
              key="filterNonEmptyValuesColumn"
              onClick={() =>
                emptyNonEmptyFilterHandler(
                  FILTER_NOT_EMPTY_PLACEHOLDER,
                  isNotEmptyFilterValueColumn(contextMenuColumn)
                )
              }
            >
              <Icon name="filter" color="olive" />
              {!isNotEmptyFilterValueColumn(contextMenuColumn)
                ? t('table.header.option.filter.non.empty.values')
                : t('table.header.option.filter.non.empty.values.remove')}
            </Menu.Item>
          </Menu>
        </Popup>
      )}
    </Table.Header>
  );
};

OverviewTableHeader.propTypes = {
  filterState: filterStateType.isRequired,
  handleFilterStateChanged: PropTypes.func.isRequired,
  tableOptions: PropTypes.array.isRequired,
  renderSorting: PropTypes.bool,
  renderSelection: PropTypes.bool,
  selectedRows: PropTypes.object.isRequired,
  onSelectAllRows: PropTypes.func.isRequired
};

OverviewTableHeader.defaultProps = {
  renderSorting: true
};

export default wrapConsumer(withTranslation()(OverviewTableHeader));
